import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react'
import { Loader2, Play, Zap, Trash2, PlayCircle, Copy } from 'lucide-react'
import { IAPIDataset, IAPIDatasetItem, IAPIDatasetItemRun } from 'extra/types/ApiModels'
import { api } from '../../api/client'
import { useEntities } from '../../context/EntitiesContext'
import { ChatHistoryComponent, ObjCell, EditableJsonCell } from '../../components/DataDisplayComponents'
import { useMultiSelect } from '../../hooks/useMultiSelect'
import { createProcessQueue } from '../../utils/processQueue'

function getRunForTarget (item: IAPIDatasetItem, targetId: string): IAPIDatasetItemRun | undefined {
  return item.runs?.find(r => r.targetId === targetId)
}

function updateItemRun (items: Array<IAPIDatasetItem>, itemId: string, run: IAPIDatasetItemRun): Array<IAPIDatasetItem> {
  return items.map(item => {
    if (item.id !== itemId) return item
    const runs = item.runs ?? []
    const existingIndex = runs.findIndex(r => r.id === run.id)
    const updatedRuns = existingIndex >= 0
      ? runs.map((r, i) => i === existingIndex ? run : r)
      : [...runs, run]
    return { ...item, runs: updatedRuns }
  })
}

function clearItemRuns (items: Array<IAPIDatasetItem>, opts?: { itemIds?: Array<string>, targetIds?: Array<string> }): Array<IAPIDatasetItem> {
  return items.map(item => {
    if (opts?.itemIds && !opts.itemIds.includes(item.id)) return item
    if (!opts?.targetIds) return { ...item, runs: [] }
    return { ...item, runs: (item.runs ?? []).filter(r => !opts.targetIds!.includes(r.targetId)) }
  })
}

export function DatasetItems ({ dataset }: { dataset: IAPIDataset }) {
  const { variants: allVariants, deployments: allDeployments } = useEntities()
  const [items, setItems] = useState<Array<IAPIDatasetItem>>([])
  const [loading, setLoading] = useState(true)

  const variants = useMemo(() => allVariants.filter(v => v.promptId === dataset.promptId), [allVariants, dataset.promptId])
  const deployments = useMemo(() => allDeployments.filter(d => d.promptId === dataset.promptId), [allDeployments, dataset.promptId])

  const { selectedIds, setSelectedIds, toggleSelect, toggleSelectAll } = useMultiSelect()
  const [executingIds, setExecutingIds] = useState<Set<string>>(new Set())
  const [evaluatingIds, setEvaluatingIds] = useState<Set<string>>(new Set())

  const executeQueueRef = useRef(createProcessQueue<IAPIDatasetItemRun>({
    onItemComplete: (_id, run) => {
      setItems(prev => updateItemRun(prev, run.datasetItemId, run))
    },
    onItemError: (id, e) => {
      console.error(`Failed to execute item ${id}`, e)
    },
    onItemFinally: (id) => {
      setExecutingIds(prev => {
        const next = new Set(prev)
        next.delete(id)
        return next
      })
    },
  }))

  const evaluateQueueRef = useRef(createProcessQueue<IAPIDatasetItemRun>({
    onItemComplete: (_id, run) => {
      setItems(prev => updateItemRun(prev, run.datasetItemId, run))
    },
    onItemError: (id, e) => {
      console.error(`Failed to evaluate run ${id}`, e)
    },
    onItemFinally: (id) => {
      setEvaluatingIds(prev => {
        const next = new Set(prev)
        next.delete(id)
        return next
      })
    },
  }))

  const executeAndEvalQueueRef = useRef(createProcessQueue<IAPIDatasetItemRun>({
    onItemComplete: (_id, run) => {
      setItems(prev => updateItemRun(prev, run.datasetItemId, run))
    },
    onItemError: (id, e) => {
      console.error(`Failed to execute+evaluate ${id}`, e)
    },
    onItemFinally: (id) => {
      setExecutingIds(prev => {
        const next = new Set(prev)
        next.delete(id)
        return next
      })
    },
  }))

  useEffect(() => {
    setLoading(true)
    api.datasets.listItems(dataset.id)
      .then(itemsData => setItems(itemsData))
      .catch(console.error)
      .finally(() => setLoading(false))
  }, [dataset.id])

  const associatedIds = useMemo(() => [
    ...(dataset.promptVariantIds || []).filter(id => variants.some(v => v.id === id)),
    ...(dataset.deploymentIds || []).filter(id => deployments.some(d => d.id === id)),
  ], [dataset.promptVariantIds, dataset.deploymentIds, variants, deployments])

  const getTargetType = useCallback((id: string): 'variant' | 'deployment' => {
    if (dataset.promptVariantIds?.includes(id)) return 'variant'
    return 'deployment'
  }, [dataset.promptVariantIds])

  const executeQueue = async (itemIds: Array<string>, opts?: { targetIds?: Array<string> }) => {
    const targetIds = opts?.targetIds ?? associatedIds

    await api.datasets.cleanup(dataset.id, {
      itemIds: itemIds,
      targetIds: targetIds,
    })
    setItems(prev => clearItemRuns(prev, { itemIds, targetIds }))

    const taskIds: Array<string> = []
    for (const itemId of itemIds) {
      for (const targetId of targetIds) {
        taskIds.push(`${itemId}::${targetId}`)
      }
    }

    setExecutingIds(prev => {
      const next = new Set(prev)
      taskIds.forEach(id => next.add(id))
      return next
    })

    executeQueueRef.current.add(taskIds, async (taskId) => {
      const [itemId, targetId] = taskId.split('::')
      return api.datasets.executeItem(itemId, { targetId, targetType: getTargetType(targetId) })
    })
  }

  const evaluateQueue = (itemIds: Array<string>, opts?: { targetIds?: Array<string> }) => {
    const targetIds = opts?.targetIds ?? associatedIds
    const taskIds: Array<string> = []

    for (const itemId of itemIds) {
      const item = items.find(i => i.id === itemId)
      if (!item?.runs) continue
      for (const run of item.runs) {
        if (targetIds.includes(run.targetId) && run.result && !run.result.error) {
          taskIds.push(run.id)
        }
      }
    }

    setEvaluatingIds(prev => {
      const next = new Set(prev)
      taskIds.forEach(id => next.add(id))
      return next
    })

    evaluateQueueRef.current.add(taskIds, (runId) => api.datasets.evaluateRun(runId))
  }

  const executeAndEvaluateQueue = async (itemIds: Array<string>, opts?: { targetIds?: Array<string> }) => {
    const targetIds = opts?.targetIds ?? associatedIds

    await api.datasets.cleanup(dataset.id, {
      itemIds: itemIds,
      targetIds: targetIds,
    })
    setItems(prev => clearItemRuns(prev, { itemIds, targetIds }))

    const taskIds: Array<string> = []
    for (const itemId of itemIds) {
      for (const targetId of targetIds) {
        taskIds.push(`${itemId}::${targetId}`)
      }
    }

    setExecutingIds(prev => {
      const next = new Set(prev)
      taskIds.forEach(id => next.add(id))
      return next
    })

    executeAndEvalQueueRef.current.add(taskIds, async (taskId) => {
      const [itemId, targetId] = taskId.split('::')
      const run = await api.datasets.executeItem(itemId, { targetId, targetType: getTargetType(targetId) })
      setItems(prev => updateItemRun(prev, run.datasetItemId, run))
      if (run.result && !run.result.error) {
        const evaluatedRun = await api.datasets.evaluateRun(run.id)
        return evaluatedRun
      }
      return run
    })
  }

  const handleDuplicateItem = async (itemId: string) => {
    try {
      const duplicated = await api.datasets.duplicateItem(itemId)
      setItems(prev => [duplicated, ...prev])
    } catch (e) {
      console.error('Failed to duplicate item', e)
    }
  }

  const handleDeleteSelected = async () => {
    if (selectedIds.length === 0) return
    if (!window.confirm(`Are you sure you want to delete ${selectedIds.length} items?`)) return

    try {
      await api.datasets.deleteItems(selectedIds)
      setItems(prev => prev.filter(i => !selectedIds.includes(i.id)))
      setSelectedIds([])
    } catch (e) {
      console.error('Failed to delete items', e)
      alert('Failed to delete items')
    }
  }

  const getVariantName = useCallback((id: string) => variants.find(v => v.id === id)?.name || id.slice(0, 8), [variants])
  const getDeploymentName = useCallback((id: string) => deployments.find(d => d.id === id)?.label || id.slice(0, 8), [deployments])

  const evalIds = dataset.evalIds || []

  const calculateMetrics = (values: Array<number>) => {
    if (values.length === 0) return { avg: 0, p90: 0, median: 0 }
    const sum = values.reduce((a, b) => a + b, 0)
    const avg = sum / values.length
    const sorted = [...values].sort((a, b) => a - b)
    const p90Index = Math.ceil(values.length * 0.9) - 1
    const p90 = sorted[p90Index]

    const middle = Math.floor(sorted.length / 2)
    const median = sorted.length % 2 === 0
      ? (sorted[middle - 1] + sorted[middle]) / 2
      : sorted[middle]

    return { avg, p90, median }
  }

  const metrics = useMemo(() => {
    const result: Record<string, {
      latency: { avg: number, p90: number, median: number }
      evals: Record<string, { avg: number, p90: number, median: number }>
    }> = {}

    associatedIds.forEach(id => {
      const latencies: Array<number> = []
      const evalScores: Record<string, Array<number>> = {}

      items.forEach(item => {
        const run = getRunForTarget(item, id)
        if (run?.metrics?.latency) latencies.push(run.metrics.latency)

        if (run?.evalResults) {
          Object.entries(run.evalResults).forEach(([evalId, val]) => {
            let score: any = val
            if (val && typeof val === 'object' && 'score' in val) {
              score = val.score
            }

            if (typeof score === 'number') {
              if (!evalScores[evalId]) evalScores[evalId] = []
              evalScores[evalId].push(score)
            }
          })
        }
      })

      result[id] = {
        latency: calculateMetrics(latencies),
        evals: {},
      }
      Object.keys(evalScores).forEach(evalId => {
        result[id].evals[evalId] = calculateMetrics(evalScores[evalId])
      })
    })

    return result
  }, [items, associatedIds])

  const getScoreColor = (score: number) => {
    const hue = score * 120
    return `hsl(${hue}, 80%, 60%)`
  }

  const handleUpdateItem = async (itemId: string, updates: Partial<IAPIDatasetItem>) => {
    try {
      await api.datasets.updateItem(itemId, updates)
      setItems(prev => prev.map(i => i.id === itemId ? { ...i, ...updates } : i))
    } catch (e) {
      console.error('Failed to update item', e)
      alert('Failed to update item')
    }
  }

  if (loading) return <div className="text-gray-400">Loading items...</div>
  if (items.length === 0) {
    return <div className="text-gray-500">No items in this dataset. Copy executions here to
    start.</div>
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center gap-2 bg-gray-800/50 p-2 rounded-lg border border-gray-700 flex-wrap">
        <button
          onClick={() => executeQueue(selectedIds)}
          disabled={selectedIds.length === 0}
          className="btn btn-primary text-sm flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Play className="w-4 h-4"/>
          Execute Selected ({selectedIds.length})
        </button>
        <button
          onClick={() => executeQueue(items.map(i => i.id))}
          className="btn btn-secondary text-sm flex items-center gap-2"
        >
          <Play className="w-4 h-4"/>
          Execute All
        </button>
        <button
          onClick={() => evaluateQueue(selectedIds)}
          disabled={selectedIds.length === 0}
          className="btn btn-secondary text-sm flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed ml-4"
        >
          <Zap className="w-4 h-4"/>
          Evaluate Selected ({selectedIds.length})
        </button>
        <button
          onClick={() => evaluateQueue(items.map(i => i.id))}
          className="btn btn-secondary text-sm flex items-center gap-2"
        >
          <Zap className="w-4 h-4"/>
          Evaluate All
        </button>
        <button
          onClick={() => executeAndEvaluateQueue(selectedIds)}
          disabled={selectedIds.length === 0}
          className="btn btn-secondary text-sm flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed ml-4"
        >
          <PlayCircle className="w-4 h-4"/>
          Exec+Eval Selected ({selectedIds.length})
        </button>
        <button
          onClick={() => executeAndEvaluateQueue(items.map(i => i.id))}
          className="btn btn-secondary text-sm flex items-center gap-2"
        >
          <PlayCircle className="w-4 h-4"/>
          Exec+Eval All
        </button>
        {selectedIds.length > 0 && (
          <button
            onClick={handleDeleteSelected}
            className="btn btn-danger text-sm flex items-center gap-2 ml-4"
          >
            <Trash2 className="w-4 h-4"/>
            Delete Selected ({selectedIds.length})
          </button>
        )}
        <div className="ml-auto text-sm text-gray-400 flex gap-4">
          {executingIds.size > 0 && <span className="flex items-center gap-2 text-blue-400"><Loader2
            className="w-4 h-4 animate-spin"/> Executing {executingIds.size}...</span>}
          {evaluatingIds.size > 0 && <span className="flex items-center gap-2 text-yellow-400"><Loader2
            className="w-4 h-4 animate-spin"/> Evaluating {evaluatingIds.size}...</span>}
        </div>
      </div>

      <div className="overflow-x-auto border border-gray-700 rounded-lg">
        <table className="w-full text-left text-sm border-collapse">
          <thead className="bg-gray-800 sticky top-0 z-10">
          <tr>
            <th className="p-2 border-b border-gray-700 w-8 text-center" rowSpan={2}>
              <input type="checkbox" checked={items.length > 0 && selectedIds.length === items.length}
                     onChange={() => toggleSelectAll(items)}/>
            </th>
            <th className="p-2 border-b border-gray-700 font-medium text-gray-400" rowSpan={2}>ID</th>
            <th className="p-2 border-b border-gray-700 font-medium text-gray-400" rowSpan={2}>Variables</th>
            <th className="p-2 border-b border-gray-700 font-medium text-gray-400" rowSpan={2}>History</th>
            <th className="p-2 border-b border-gray-700 font-medium text-gray-400" rowSpan={2}>Eval Settings</th>
            {associatedIds.map(id => (
              <th key={id} colSpan={2 + evalIds.length}
                  className="p-2 border-b border-gray-700 font-medium text-center border-l border-gray-600">
                <div className="flex items-center justify-center gap-2">
                  <span>
                    {dataset.promptVariantIds?.includes(id) ? 'Variant: ' : 'Deployment: '}
                    {dataset.promptVariantIds?.includes(id) ? getVariantName(id) : getDeploymentName(id)}
                  </span>
                  <button onClick={() => executeQueue(items.map(i => i.id), { targetIds: [id] })} title="Execute all items for this column"
                          className="text-gray-400 hover:text-blue-400">
                    <Play className="w-3 h-3"/>
                  </button>
                  {evalIds.length > 0 && (
                    <button onClick={() => evaluateQueue(items.map(i => i.id), { targetIds: [id] })} title="Evaluate all items for this column"
                            className="text-gray-400 hover:text-yellow-400">
                      <Zap className="w-3 h-3"/>
                    </button>
                  )}
                </div>
              </th>
            ))}
          </tr>
          <tr>
            {associatedIds.map(id => (
              <React.Fragment key={id}>
                <th className="p-2 border-b border-gray-700 font-medium text-gray-400 border-l border-gray-600">Output
                </th>
                <th className="p-2 border-b border-gray-700 font-medium text-gray-400">
                  Latency
                  <div className="text-[10px] text-gray-500 font-normal whitespace-nowrap">
                    Avg: {metrics[id]?.latency.avg.toFixed(0)}ms<br/>
                    Med: {metrics[id]?.latency.median.toFixed(0)}ms<br/>
                    P90: {metrics[id]?.latency.p90.toFixed(0)}ms
                  </div>
                </th>
                {evalIds.map(evalId => (
                  <th key={evalId} className="p-2 border-b border-gray-700 font-medium text-gray-400"
                      data-tooltip={evalId}>
                    {evalId.slice(0, 8)}
                    <div className="text-[10px] text-gray-500 font-normal whitespace-nowrap">
                      Avg: <span
                      style={{ color: typeof metrics[id]?.evals[evalId]?.avg === 'number' ? getScoreColor(metrics[id]!.evals[evalId]!.avg) : undefined }}>{metrics[id]?.evals[evalId]?.avg?.toFixed(2)}</span><br/>
                      Med: <span
                      style={{ color: typeof metrics[id]?.evals[evalId]?.median === 'number' ? getScoreColor(metrics[id]!.evals[evalId]!.median) : undefined }}>{metrics[id]?.evals[evalId]?.median?.toFixed(2)}</span><br/>
                      P90: <span
                      style={{ color: typeof metrics[id]?.evals[evalId]?.p90 === 'number' ? getScoreColor(metrics[id]!.evals[evalId]!.p90) : undefined }}>{metrics[id]?.evals[evalId]?.p90?.toFixed(2)}</span>
                    </div>
                  </th>
                ))}
              </React.Fragment>
            ))}
          </tr>
          </thead>
          <tbody className="divide-y divide-gray-700">
          {items.map(item => (
            <tr key={item.id} className={`hover:bg-gray-800/50 transition-colors ${selectedIds.includes(item.id) ? 'bg-blue-900/30' : ''}`}>
              <td className="p-2 align-top text-center">
                <input type="checkbox" checked={selectedIds.includes(item.id)} onChange={() => toggleSelect(item.id)}/>
              </td>
              <td className="p-2 align-top text-xs text-gray-500 font-mono">
                <div className="flex items-center gap-1">
                  {item.id.slice(-8)}
                  <button
                    onClick={() => handleDuplicateItem(item.id)}
                    title="Duplicate item"
                    className="text-gray-500 hover:text-gray-300 cursor-pointer"
                  >
                    <Copy className="w-3 h-3" />
                  </button>
                </div>
                {executingIds.size > 0 && Array.from(executingIds).some(id => id.startsWith(item.id)) && <Loader2 className="w-3 h-3 animate-spin text-blue-500 mt-1"/>}
                {evaluatingIds.size > 0 && Array.from(evaluatingIds).some(id => {
                  const run = item.runs?.find(r => r.id === id)
                  return !!run
                }) && <Loader2 className="w-3 h-3 animate-spin text-yellow-500 mt-1"/>}
              </td>
              <td className="p-2 align-top"><ObjCell data={item.variablesValues}/></td>
              <td className="p-2 align-top">
                <div className=" custom-scrollbar">
                  <ChatHistoryComponent history={item.chatHistory}/>
                </div>
              </td>
              <td className="p-2 align-top">
                <div className="flex flex-col gap-2">
                  {(dataset.evalIds || []).map(evalId => (
                    <div key={evalId}>
                      <div className="text-[10px] text-gray-400 mb-1">{evalId}</div>
                      <EditableJsonCell
                        initialValue={item.evalConfigs?.[evalId] || {}}
                        onSave={(val) => handleUpdateItem(item.id, {
                          evalConfigs: {
                            ...(item.evalConfigs || {}),
                            [evalId]: val,
                          },
                        })}
                      />
                    </div>
                  ))}
                  {(dataset.evalIds || []).length === 0 &&
                    <span className="text-gray-500 text-xs italic">No evaluators enabled</span>}
                </div>
              </td>
              {associatedIds.map(id => {
                const run = getRunForTarget(item, id)
                const result = run?.result
                return (
                  <React.Fragment key={id}>
                    <td
                      className="p-2 align-top border-l border-gray-700 custom-scrollbar ">
                      <ChatHistoryComponent history={result?.output ? [result.output] : null}/>
                    </td>
                    <td className="p-2 align-top text-xs">
                      {run?.metrics?.latency ? `${run.metrics.latency}ms` : '-'}
                    </td>
                    {evalIds.map(evalId => {
                      let score: any = run?.evalResults?.[evalId]
                      if (score && typeof score === 'object' && 'score' in score) score = score.score
                      return (
                        <td key={evalId} className="p-2 align-top text-xs font-bold"
                            style={{ color: typeof score === 'number' ? getScoreColor(score) : 'inherit' }}>
                          {typeof score === 'number' ? score.toFixed(2) : '-'}
                        </td>
                      )
                    })}
                  </React.Fragment>
                )
              })}
            </tr>
          ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}
